import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useUpdateTherapyComplianceByPatient } from '../../../../../api/created/therapyCompliance.ts';
import { TherapyComplianceDto, TherapyComplianceDtoTherapyCompliance } from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useCreateTherapyComplianceByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getTherapyComplianceStatusText } from '../../../../../utils/compliance.ts';
import { formatDateForInput, getCurrentDate } from '../../../../../utils/date.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';

interface FormValues {
  date: string;
  compliance: TherapyComplianceDtoTherapyCompliance;
  file: File | null;
}

function getDefaultValues(currentCompliance?: TherapyComplianceDto): FormValues {
  if (currentCompliance) {
    return {
      date: formatDateForInput(currentCompliance.date),
      compliance: currentCompliance.therapyCompliance,
      file: null,
    };
  }

  return {
    date: formatDateForInput(getCurrentDate()),
    compliance: undefined as unknown as TherapyComplianceDtoTherapyCompliance,
    file: null,
  };
}

export function AddComplianceDialog({
  open,
  onClose,
  patientId,
  currentCompliance,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  currentCompliance?: TherapyComplianceDto;
}) {
  const queryClient = useQueryClient();
  const isEditing = !!currentCompliance?.id;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(currentCompliance),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const invalidateTherapy = () => {
    void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
  };

  const createComplianceMutation = useCreateTherapyComplianceByPatient({
    mutation: {
      onSuccess: () => {
        invalidateTherapy();
        handleOnClose();
      },
      onError: () => toast.error('Beim Erfassen der Compliance ist ein Fehler aufgetreten'),
    },
  });

  const updateComplianceMutation = useUpdateTherapyComplianceByPatient({
    onSuccess: () => {
      invalidateTherapy();
      handleOnClose();
    },
    onError: () => toast.error('Beim Bearbeiten der Compliance ist ein Fehler aufgetreten'),
  });

  const handleOnClose = () => {
    reset(getDefaultValues());
    onClose();
  };

  const handleOnSubmit = async (data: FormValues) => {
    if (isEditing && currentCompliance) {
      updateComplianceMutation.mutate({
        patientId,
        complianceId: currentCompliance.id,
        data: {
          date: data.date,
          therapyCompliance: data.compliance,
          file: data.file
            ? {
                fileName: data.file.name,
                file: await blobToBase64(data.file),
              }
            : null,
        },
      });
      return;
    }

    createComplianceMutation.mutate({
      id: patientId,
      data: {
        date: data.date,
        therapyCompliance: data.compliance,
        file: data.file
          ? {
              fileName: data.file.name,
              file: await blobToBase64(data.file),
            }
          : undefined,
      },
    });
  };

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(currentCompliance));
    }
  }, [open, currentCompliance, reset]);

  const isLoading = createComplianceMutation.isPending || updateComplianceMutation.isPending;

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 750 }}>
        <ModalClose />
        <Typography level={'h4'}>{isEditing ? 'Compliance bearbeiten' : 'Compliance erfassen'}</Typography>
        <Divider />
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="date"
                  rules={{
                    required: true,
                  }}
                />
                {errors.date && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.compliance}>
                <FormLabel>
                  <Typography>Compliance *</Typography>
                </FormLabel>
                <Controller
                  name={'compliance'}
                  rules={{ required: true }}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select value={value ?? null} onChange={(_, selectedValue) => onChange(selectedValue)}>
                      {Object.values(TherapyComplianceDtoTherapyCompliance).map((type) => (
                        <Option value={type} key={type}>
                          {getTherapyComplianceStatusText(type)}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.compliance && <FormHelperText>Compliance muss angegeben werden</FormHelperText>}
              </FormControl>
              <FormControl sx={{ gridColumn: '1 / span 2' }}>
                <FormLabel>
                  <Typography>Datei</Typography>
                </FormLabel>
                <Controller
                  name="file"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept="application/pdf"
                      placeholder={currentCompliance?.fileDetails?.fileName ?? 'Datei hochladen'}
                    />
                  )}
                />
              </FormControl>
            </FormGrid>
            <FormActionButtons onAbortClick={handleOnClose} disableSubmit={!isFormDirty} isLoading={isLoading} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
